import { ProductReflectionsQuery } from '@graphql/queries/__generated__/reflection.generated'
import Skeleton from '@mui/material/Skeleton'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import DOMPurify from 'dompurify'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import ReflectionCard from '../../components/Product/ReflectionCard'
import useLocalStorage from '../../hooks/useLocalStorage'
import { AdWithImageFragment } from '@graphql/fragments/__generated__/Ad.generated'
import { Link } from 'react-router-dom'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import PlaceHolder from 'images/placeholder-img.svg'
import { CLIENT_URL } from '../../../constants'
import { ProductListImageSizeEnum } from '../../../generated/gqlTypes'

export const STORAGE_GRID_NAME = 'categoryGridClass'

export enum REFLECTIONS_IMG_SIZE_ENUM {
  SMALL = 'product-grid_7',
  MEDIUM = 'product-grid_5',
  LARGE = 'product-grid_3',
}

export const DEFAULT_IMG_SIZE = REFLECTIONS_IMG_SIZE_ENUM.MEDIUM

export const IMG_SIZE_TO_BACKEND_ENUM: Record<REFLECTIONS_IMG_SIZE_ENUM, ProductListImageSizeEnum> = {
  [REFLECTIONS_IMG_SIZE_ENUM.SMALL]: ProductListImageSizeEnum.Small,
  [REFLECTIONS_IMG_SIZE_ENUM.MEDIUM]: ProductListImageSizeEnum.Medium,
  [REFLECTIONS_IMG_SIZE_ENUM.LARGE]: ProductListImageSizeEnum.Large,
}

export const BACKEND_ENUM_TO_IMG_SIZE: Record<ProductListImageSizeEnum, REFLECTIONS_IMG_SIZE_ENUM> = {
  [ProductListImageSizeEnum.Small]: REFLECTIONS_IMG_SIZE_ENUM.SMALL,
  [ProductListImageSizeEnum.Medium]: REFLECTIONS_IMG_SIZE_ENUM.MEDIUM,
  [ProductListImageSizeEnum.Large]: REFLECTIONS_IMG_SIZE_ENUM.LARGE,
}

type IReflectionEdges = ProductReflectionsQuery['specifics']['edges']

interface IProps {
  specificsData: IReflectionEdges
  imgSizeClass: REFLECTIONS_IMG_SIZE_ENUM
  loading?: boolean
  categoryDescription?: string
  ads: AdWithImageFragment[]

  onAdClick?(adId: string): void
}

const range = _.range(20)
const ReflectionsListSkeleton: React.FC = () => {
  return (
    <>
      {range.map((item, i) => (
        <div key={i}>
          <div className='relative mb-10' style={{ paddingBottom: '138%' }}>
            <Skeleton className='absolute top-0 bottom-0 left-0 right-0' variant='rectangular' height='100%' />
          </div>
          <Skeleton variant='text' />
          <Skeleton variant='text' />
        </div>
      ))}
    </>
  )
}

type MEDIA_SIZE = {
  xs: boolean
  sm: boolean
  md: boolean
  lg: boolean
}

const getIndexForGridRow = (mediaSize: MEDIA_SIZE, gridClass: REFLECTIONS_IMG_SIZE_ENUM): number => {
  switch (gridClass) {
    case REFLECTIONS_IMG_SIZE_ENUM.SMALL:
      if (mediaSize.xs) {
        return 2
      } else if (mediaSize.sm) {
        return 3
      } else if (mediaSize.md) {
        return 5
      } else {
        return 7
      }
    case REFLECTIONS_IMG_SIZE_ENUM.MEDIUM:
      if (mediaSize.xs) {
        return 2
      } else if (mediaSize.sm) {
        return 3
      } else if (mediaSize.md) {
        return 4
      } else {
        return 5
      }
    case REFLECTIONS_IMG_SIZE_ENUM.LARGE:
      if (mediaSize.xs) {
        return 2
      } else if (mediaSize.sm) {
        return 2
      } else if (mediaSize.md) {
        return 3
      } else {
        return 3
      }
  }
  return 7
}

const SpecificsList: React.FC<IProps> = ({
  specificsData,
  imgSizeClass,
  loading,
  categoryDescription,
  ads,
  onAdClick,
}) => {
  const theme = useTheme()
  const [, , getStorageValue] = useLocalStorage(STORAGE_GRID_NAME, DEFAULT_IMG_SIZE)
  const xs = useMediaQuery(theme.breakpoints.only('xs'))
  const sm = useMediaQuery(theme.breakpoints.only('sm'))
  const md = useMediaQuery(theme.breakpoints.only('md'))
  const lg = useMediaQuery(theme.breakpoints.up('lg'))
  const [adPositions, setAdPositions] = useState<{ [key: number]: AdWithImageFragment }>({})
  const onGooglePushDataLayer = (reflection, category, position, action) => e => {
    // googlePushProductClick(reflection, category, position, action)
  }
  const showBigImg =
    [REFLECTIONS_IMG_SIZE_ENUM.LARGE, REFLECTIONS_IMG_SIZE_ENUM.MEDIUM].includes(imgSizeClass || getStorageValue()) ||
    sm ||
    xs
  const indexRow = getIndexForGridRow(
    {
      xs,
      sm,
      md,
      lg,
    },
    imgSizeClass || getStorageValue()
  )

  useEffect(() => {
    if (!ads.length) {
      setAdPositions({})
      return
    }
    if (specificsData.length) {
      const result = { ...adPositions }
      const currentAdsIds = Object.values(adPositions).map(ad => ad.id)
      const newAds = ads.filter(ad => !currentAdsIds.includes(ad.id))
      const minIndex = Number(_.max([...Object.keys(adPositions), 7]))

      newAds.forEach(ad => {
        const position = _.random(minIndex, specificsData.length - 1)
        result[position] = ad
      })
      setAdPositions(prevState => ({ ...prevState, ...result }))
    }
  }, [ads.length, specificsData.length])

  return (
    <>
      {!specificsData?.length && !loading && (
        <Typography variant='h5'>Sorry, no matches found for your request. Try again or shop new arrivals.</Typography>
      )}
      <div className={`category-products__list product-grid ${imgSizeClass || getStorageValue()}`}>
        {specificsData.map((el, i) => {
          const spec = el.node
          const ad = adPositions[i]
          const adSmallImageUrl = ad?.images.filter(img => img.device === 'xs')[0]?.image
          const adBigImageUrl = ad?.images.filter(img => img.device !== 'xs')[0]?.image
          const isExternalAd = !ad?.url.includes(CLIENT_URL)
          return (
            <React.Fragment key={spec.id}>
              {!!categoryDescription && i === indexRow && (
                <div className='grid-row flex items-center py-24'>
                  <div
                    className='description'
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(categoryDescription),
                    }}
                  />
                </div>
              )}
              <div className='product-thumb text-center'>
                <ReflectionCard
                  spec={spec}
                  onGooglePushDataLayer={onGooglePushDataLayer}
                  showBigImg={showBigImg}
                  idx={i}
                  showFavoritesButton
                />
              </div>
              {ad && (
                <div className='product-thumb text-center'>
                  <div className={`relative`} style={{ paddingBottom: '138%' }}>
                    <a
                      // to={isExternalAd ? undefined : new URL(ad.url).pathname}
                      href={ad.url}
                      onClick={() => onAdClick(ad.id)}
                      aria-label='product'
                      style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}
                    >
                      {showBigImg && (
                        <LazyLoadImage
                          alt={ad.title}
                          effect='opacity'
                          placeholderSrc={PlaceHolder}
                          src={adBigImageUrl || adSmallImageUrl || PlaceHolder}
                        />
                      )}
                      {!showBigImg && (
                        <LazyLoadImage
                          alt={ad.title}
                          effect='opacity'
                          placeholderSrc={PlaceHolder}
                          src={adSmallImageUrl || adBigImageUrl || PlaceHolder}
                        />
                      )}
                    </a>
                  </div>
                  <div className='caption'>
                    <h4 itemProp='name'>
                      <Link className='animsition-link line-clamp-3' to={ad.url}>
                        {ad.title}
                      </Link>
                    </h4>
                  </div>
                </div>
              )}
            </React.Fragment>
          )
        })}
        {!!specificsData && !!categoryDescription && specificsData.length <= indexRow && (
          <div className='grid-row flex items-center py-24'>
            <div
              className='description'
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(categoryDescription),
              }}
            />
          </div>
        )}
        {loading && !specificsData?.length && <ReflectionsListSkeleton />}
      </div>
    </>
  )
}

export default SpecificsList
